<template lang="pug">
    .image-uploader
        .cropper-wrapper
            v-progress-circular.loader(
                v-show="imageLoading"
                :size="50"
                indeterminate
            )
            v-cropper(
                v-model="cropper"
                :width="width"
                :height="height"
                :style="cropperStyle"
                :prevent-white-space="true"
                :accept="'image/*'"
                :initial-image="noCacheImageUrl"
                :placeholder="placeholder"
                :placeholder-font-size="16"
                :show-remove-button="false"
                :quality='3.5',
                :disabled="imageLoading"
                :file-size-limit='fileSizeLimitInBytes'
                initial-size="cover"
                @file-choose="onImageChoose"
                @draw="handleImageCrop"
                @file-size-exceed="onFileSizeExceeded"
                @loading-start="onLoadingStart"
                @loading-end="onLoadingEnd"
            )
        .browse-file
            v-text-field.browse-file__field(
                v-model="chosenFileName"
                :label="inputLabel"
                :error-messages="errorMessage"
                :class="inputClasses"
                validate-on-blur
                persistent-hint
                readonly
            )
            v-btn.browse-file__btn(@click="onBrowseBtnClick" dark) {{ $vuetify.lang.t('$vuetify.buttons.browse') }}...
</template>

<script>
import Cropper from 'vue-croppa';
import {debounce} from "@/helpers";

const vCropper = Cropper.component;

const DEFAULT_FILE_EXTENSION = 'file.png';
const FILE_SIZE_LIMIT_MB = 5;
const BYTES_IN_MB = 1000000;
const FILE_SIZE_LIMIT_BYTES = FILE_SIZE_LIMIT_MB * BYTES_IN_MB;

export default {
    name: 'ImageUploader',
    components: {
        vCropper,
    },
    props: {
        width: {
            type: Number,
            required: true
        },
        height: {
            type: Number,
            required: true
        },
        inputLabel: {
            type: String,
            default: 'Image'
        },
        initialImage: {
            type: String,
            default: null
        },
        errorMessage: {
            type: String,
            default: null
        }
    },
    data: () => ({
        cropper: {},
        chosenFileName: null,
        isError: false,
        imageLoading: false,
        fileSizeLimitInBytes: FILE_SIZE_LIMIT_BYTES,
        noCacheImageUrl: ''
    }),
    computed: {
        placeholder() {
            return this.imageLoading ? '' : this.$vuetify.lang.t('$vuetify.chooseImage');
        },
        cropperStyle() {
            return {
                maxHeight: `${this.height}px`
            };
        },
        inputClasses() {
            return {
                invalid: !!this.errorMessage
            };
        }
    },
    created() {
        if (this.initialImage) {
            // TODO: Find a better solution
            const imageUniqueKey = new Date().getTime();
            this.noCacheImageUrl = `${this.initialImage}?t=${imageUniqueKey}`;
            this.chosenFileName = DEFAULT_FILE_EXTENSION;
        }

        this.handleImageCrop = debounce(this.handleImageCrop, 150);
    },
    methods: {
        onLoadingStart() {
            this.imageLoading = true;
        },
        onLoadingEnd() {
            this.imageLoading = false;
        },
        onBrowseBtnClick() {
            this.cropper.chooseFile();
        },
        async onImageChoose() {
          await this.$nextTick();
          const chosenFile = this.cropper.getChosenFile();
          if (chosenFile.size > FILE_SIZE_LIMIT_BYTES) {
            return;
          }
          this.chosenFileName = chosenFile.name;
          this.$emit('choose-image', new File([await this.cropper.promisedBlob()], chosenFile.name));
        },
        onFileSizeExceeded() {
          this.$store.dispatch('alert/addMessage', {
            message: this.$vuetify.lang.t('$vuetify.messages.error.fileSizeExceeded', FILE_SIZE_LIMIT_MB),
            locale: false
          });
        },
        async handleImageCrop() {
            this.$emit('choose-image', new File([await this.cropper.promisedBlob()], this.chosenFileName));
        }
    }
};
</script>

<style lang="scss" scoped>
.image-uploader {
    display: flex;
    flex-direction: column;
    position: relative;
}

.cropper-wrapper {
    position: relative;
    max-width: fit-content;

    .loader {
        position: absolute;
        top: 50%;
        left: 50%;
        z-index: 100;
        transform: translate(-50%, -50%);
    }
}

.browse-file {
    display: flex;
    justify-content: flex-start;

    &__field {
        max-width: 280px;
        pointer-events: none;
    }

    &__btn {
        margin-top: 10px;
        margin-left: 10px;
    }
}

::v-deep {
    .sk-fading-circle {
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }
}
</style>
